/***
 *
 *   APP LAYOUT
 *   Main application layout: a primary-coloured navigation frame with the
 *   page as an inset rounded card. Desktop uses the left sidebar; mobile
 *   uses the bottom bar inside the card. Ported from
 *   ui-reference/src/components/layouts/app-layout.tsx, then restyled so
 *   the nav colour wraps the content the way the dashboard sketch does.
 *
 *   The nav item list is shared by the sidebar and the mobile bottom bar, as in
 *   the reference. Sign out is NOT in it any more — it lives in the top bar's
 *   user menu, which is where the reference puts it.
 *
 *   PROPS
 *   title: title of the view
 *
 **********/

import { useContext } from 'react';
import { AuthContext } from 'app/auth';
import { AppNav, Header, MobileBottomBar, ContentContainer, cn } from 'components/lib';
import { SidebarContextProvider } from 'context/SidebarContext';
import { useTranslate } from 'app/translations.js';
import Style from './app.tailwind.js';

function AppShell(props) {
    const { items } = props;

    return (
        <div className={Style.wrapper}>
            <AppNav items={items} />

            <div className={Style.column}>
                <Header title={props.title} />

                <main className={Style.main}>
                    {/* Horizontal gutter and vertical rhythm for every screen that
                        has not been migrated to its own ContentContainer yet.
                        Phases 4-10 move this into the views themselves, where a
                        full-height board can opt out of it.

                        `flex min-h-0 flex-1 flex-col` keeps the height chain
                        intact so Phase 4's tickets board can fill the viewport
                        and scroll inside its columns rather than the page. */}
                    <ContentContainer className={cn(Style.content, 'flex min-h-0 flex-1 flex-col')}>
                        {<props.children {...props.data} />}
                    </ContentContainer>
                </main>

                <MobileBottomBar items={items} />
            </div>
        </div>
    );
}

export function AppLayout(props) {
    const { _t } = useTranslate();
    const { permission } = useContext(AuthContext);

    const items = [
        { key: 'tickets', label: _t('ticket.label.multiple'), icon: 'clipboard', link: '/app/tickets' },
        ...(permission.admin
            ? [{ key: 'contacts', label: _t('contact.label.multiple'), icon: 'users', link: '/app/contacts' }]
            : []),
        {
            key: 'support',
            label: _t('support.label'),
            icon: 'info',
            link: 'https://support.valetify.io/project/valetify',
            external: true,
        },
        { key: 'settings', label: _t('settings.label'), icon: 'settings', link: '/app/settings' },
    ];

    return (
        <SidebarContextProvider>
            {/* props.children is a COMPONENT here, not a node - see View */}
            <AppShell items={items} {...props} />
        </SidebarContextProvider>
    );
}
